import { Component } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { Department } from "@enums/department.enum";
import { MachineEventType } from "@enums/machineEventType.enum";
import { MachineRequestType } from "@enums/machineRequestType.enum";
import { MaterialType } from "@enums/materialType.enum";
import { ProductionStatus } from "@enums/productionStatus.enum";
import { PurchaseTypeEnum } from "@enums/purchaseType.enum";
import { environment } from "@environments/environment";
import { AppSettingsService } from "@services/appSettings.service";
import { CheckGroupPermissions } from "@helpers/checkGroupPermissions";
import { GetPropertiesFromEnum } from "@helpers/getPropertiesFromEnum";
import { AdminHeaderButton } from "@interfaces/adminHeaderButton.interface";
import { CriticalSpare } from "@interfaces/criticalSpare.interface";
import { Machine } from "@interfaces/models/machine.interface";
import { MachineAvailability } from "@interfaces/machineAvailability.interface";
import { DocumentFileLineData } from "@interfaces/data/documentFileLineData";
import { MachineEvent } from "@interfaces/machineEvent.interface";
import { MachineRequest } from "@interfaces/machineRequest.interface";
import { PurchasableItem } from "@interfaces/purchasableItem.interface";
import { Site } from "@interfaces/site.interface";
import { Supplier } from "@interfaces/models/supplier.interface";
import { UserProfile } from "@interfaces/userProfile.interface";
import { CriticalSpareService } from "@services/criticalSpare.service";
import { FileService } from "@services/file.service";
import { MachineService } from "@services/machine.service";
import { MachineAvailabilityService } from "@services/machineAvailability.service";
import { MachineEventService } from "@services/machineEvent.service";
import { MachineQuestionnaireService } from "@services/machineQuestionnaire.service";
import { MachineRequestService } from "@services/machineRequest.service";
import { OpsActionAuditService } from "@services/opsActionAudit.service";
import { PurchasableItemService } from "@services/purchasableItem.service";
import { SiteService } from "@services/site.service";
import { SupplierService } from "@services/supplier.service";
import { UserProfileService } from "@services/userProfile.service";
import { ToastService } from "angular-toastify";
import { Guid } from "guid-typescript";

@Component({
	moduleId: module.id,
	selector: "machines-id",
	styleUrls: ["machines-id.css"],
	templateUrl: "machines-id.html"
})
export class MachinesIdAdminComponent {
	canEditHourlyRate = false;
	createEnabled = true;
	criticalSpares: CriticalSpare[] = [];
	departments: any;
	documents: DocumentFileLineData[] = [];
	editEnabled = true;
	events: MachineEvent[] = [];
	headerPrimaryButtons: AdminHeaderButton[] = [];
	headerSecondaryButtons: AdminHeaderButton[] = [];
	item: Machine = new Machine();
	itemType = "Machine";
	machineAvailabilities: MachineAvailability[] = [];
	machineEventTypes: any;
	machineRequestTypes: any;
	mode = "";
	opsActionAudits: any;
	people: UserProfile[] = [];
	popupAddButtonOptions: any = { onClick: () => this.addNewEvent(), text: "Add Event", visible: true };
	popupCloseButtonOptions: any = { onClick: () => this.closePopup(), text: "Close" };
	popupCriticalSpare: CriticalSpare = new CriticalSpare();
	popupDocument: DocumentFileLineData = new DocumentFileLineData();
	popupEvent: MachineEvent = new MachineEvent();
	popupMachineAvailability: MachineAvailability = new MachineAvailability();
	popupPurchasableItem: PurchasableItem = new PurchasableItem();
	popupTitle = "Add New Event";
	popupVisible = false;
	productionStatuses: any;
	purchasableItems: PurchasableItem[] = [];
	questionnaires: any;
	readOnly = true;
	requests: MachineRequest[] = [];
	returnUrl: string = location.href.split("/")[3] + "/" + location.href.split("/")[4];
	sites: Site[] = [];
	statuses: any;
	suppliers: Supplier[] = [];
	title: string = "View " + this.itemType;

	constructor(private router: Router, private toastService: ToastService, private route: ActivatedRoute, appSettingsService: AppSettingsService, private criticalSpareService: CriticalSpareService, private fileService: FileService, private itemService: MachineService, private machineAvailabilityService: MachineAvailabilityService, private machineEventService: MachineEventService, private machineQuestionnaireService: MachineQuestionnaireService, private machineRequestService: MachineRequestService, private opsActionAuditService: OpsActionAuditService, private purchasableItemService: PurchasableItemService, private siteService: SiteService, private supplierService: SupplierService, private userProfileService: UserProfileService) {
		this.canEditHourlyRate = CheckGroupPermissions(appSettingsService, "Super Admin");
		this.buildButtons("view");
		this.departments = GetPropertiesFromEnum(Department);
		this.machineEventTypes = GetPropertiesFromEnum(MachineEventType);
		this.machineRequestTypes = GetPropertiesFromEnum(MachineRequestType);
		this.productionStatuses = GetPropertiesFromEnum(ProductionStatus);
		this.getUserProfiles();
		this.getSites();
		this.getPurchasableItems();
		this.getSuppliers();
		this.route.params
			.subscribe((params) => {
				if (params.id != "create") {
					if (params.id.split("_").length > 1) {
						this.getItem(params.id.split("_")[0]);
						this.edit();
					} else {
						this.getItem(params.id);
					}
				} else {
					this.create();
				}
			});
		
		this.deleteAvailability = this.deleteAvailability.bind(this);
		this.deleteCriticalSpare = this.deleteCriticalSpare.bind(this);
		this.deleteDocumentLink = this.deleteDocumentLink.bind(this);
		this.deleteEvent = this.deleteEvent.bind(this);
		this.downloadFileLink = this.downloadFileLink.bind(this);
		this.downloadEventThirdPartyReportLink = this.downloadEventThirdPartyReportLink.bind(this);
		this.editAvailability = this.editAvailability.bind(this);
		this.editCriticalSpare = this.editCriticalSpare.bind(this);
		this.editEvent = this.editEvent.bind(this);
		this.uploadDocument = this.uploadDocument.bind(this);
		this.uploadEventImage = this.uploadEventImage.bind(this);
		this.uploadEventThirdPartyReport = this.uploadEventThirdPartyReport.bind(this);		
		this.viewEventImage = this.viewEventImage.bind(this);
	}

	addNewAvailability() {
		this.machineAvailabilityService.createSingle(this.popupMachineAvailability)
			.subscribe(
				() => {
					this.popupVisible = false;
					this.getAvailability();
					this.toastService.success("Successfully Added Availability");
				},
				(err: any) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				}
			);
	}

	addNewAvailabilityPopup() {
		this.popupMachineAvailability = new MachineAvailability();
		this.popupMachineAvailability.machineId = this.item.id;
		this.popupTitle = "Add New Availability";
		this.popupAddButtonOptions = { onClick: () => this.addNewAvailability(), text: "Add Availability", visible: true };
		this.popupVisible = true;
	}

	addNewCriticalSpare() {
		this.criticalSpareService.createSingle(this.popupCriticalSpare)
			.subscribe(
				() => {
					this.popupVisible = false;
					this.getCriticalSpares();
					this.toastService.success("Successfully Added Availability");
				},
				(err: any) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				}
			);
	}

	addNewCriticalSparePopup() {
		this.popupCriticalSpare = new CriticalSpare();
		this.popupCriticalSpare.machineId = this.item.id;
		this.popupTitle = "Add New Critical Spare";
		this.popupAddButtonOptions = { onClick: () => this.addNewCriticalSpare(), text: "Add Spare", visible: true };
		this.popupVisible = true;
	}

	addNewDocument() {
		if (this.popupDocument.description === "") {
			alert("Please enter a description");
			return;
		}
		this.documents.push(this.popupDocument);
		this.popupDocument = new DocumentFileLineData();
		this.closePopup();
	}

	addNewDocumentPopup() {
		this.popupDocument = new DocumentFileLineData();
		this.popupTitle = "Add New Document";
		this.popupVisible = true;
		this.popupAddButtonOptions = { onClick: () => this.addNewDocument(), text: "Add Document" };
	}

	addNewEvent() {
		this.popupEvent.machineId = this.item.id;
		this.machineEventService.createSingle(this.popupEvent)
			.subscribe(
				() => {
					this.popupVisible = false;
					this.popupEvent = new MachineEvent();
					this.getEvents();
					this.toastService.success("Successfully Added Event");
				},
				(err: any) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				}
			);
	}

	addNewEventPopup() {
		this.popupEvent = new MachineEvent();
		this.popupTitle = "Add New Event";
		this.popupAddButtonOptions = { onClick: () => this.addNewEvent(), text: "Add Event", visible: true };
		this.popupVisible = true;
	}

	addNewPurchasableItem() {
		this.purchasableItemService.createSingle(this.popupPurchasableItem)
			.subscribe(
				() => {
					this.popupVisible = false;
					this.popupPurchasableItem = new PurchasableItem();
					this.getPurchasableItems();
				},
				(err: any) => console.log(err)
			);
	}

	addNewPurchasableItemPopup() {
		this.popupPurchasableItem = new PurchasableItem();
		this.popupPurchasableItem.materialType = MaterialType.NotApplicable;
		this.popupPurchasableItem.purchaseType = PurchaseTypeEnum.CriticalSpares;
		this.popupTitle = "Add New Purchasable Item";
		this.popupVisible = true;
		this.popupAddButtonOptions = { onClick: () => this.addNewPurchasableItem(), text: "Add Item" };
	}

	boolYesNo(cellInfo: any) {
		if(cellInfo.value === true)
		{
			return "YES";
		}
		else
		{
			return "NO";
		}
	}

	buildButtons(mode: string) {
		this.mode = mode;
		if (mode == "edit" && this.editEnabled) {
			this.headerPrimaryButtons = [
				{ method: "updateItem", text: "Save Changes" },
				{ method: "cancelEditing", text: "View Mode" },
				{ method: "close", text: "Close" }
			];
			this.headerSecondaryButtons = [{ method: "addNewDocumentPopup", text: "Add Document" }];
		} else if (mode == "create" && this.createEnabled) {
			this.headerPrimaryButtons = [
				{ method: "createItem", text: "Save" },
				{ method: "close", text: "Close" }
			];
			this.headerSecondaryButtons = [{ method: "addNewDocumentPopup", text: "Add Document" }];
		} else if (mode == "view") {
			this.headerPrimaryButtons = [
				{ method: "edit", text: "Edit" },
				{ method: "close", text: "Close" }
			];
			this.headerSecondaryButtons = [
				{ method: "viewPFMEAs", text: "View PFMEAs" },
				{ method: "addNewEventPopup", text: "Add Event" },
				{ method: "addNewAvailabilityPopup", text: "Add Availability" },
				{ method: "addNewCriticalSparePopup", text: "Add Spare" },
				{ method: "addNewPurchasableItemPopup", text: "Create New Spare" },
			];
		}
	}

	cancelEditing() {
		if (confirm("Are you sure you wish to switch modes? Unsaved changes will be discarded.") == false) {
			return;
		}
		this.readOnly = true;
		this.buildButtons("view");
		this.title = "View " + this.itemType;
	}

	close() {
		this.router.navigate([`/${this.returnUrl}/`]);
	}

	closePopup() {
		this.popupVisible = false;
	}

	create() {
		this.readOnly = false;
		this.buildButtons("create");
		this.title = "Create New " + this.itemType;
	}

	createItem() {
		this.item.documents = JSON.stringify(this.documents);
		this.itemService.createSingle(this.item)
			.subscribe(
				() => {
					this.toastService.success("Successfully Updated " + this.itemType);
					this.item = new Machine();
					this.close();
				},
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				}
			);
	}

	deleteAvailability(e: any) {
		if (confirm("Are you sure you wish to delete this availability?")) {
			this.machineAvailabilityService.deleteSingleById(e.row.data.id)
				.subscribe(
					() => {
						this.toastService.success("Availability Deleted");
						this.getAvailability();
					},
					(err: any) => console.log(err)
				);
		}
	}

	deleteCriticalSpare(e: any) {
		if (confirm("Are you sure you wish to delete this critical spare?")) {
			this.criticalSpareService.deleteSingleById(e.row.data.id)
				.subscribe(
					() => {
						this.toastService.success("Availability Deleted");
						this.getCriticalSpares();
					},
					(err: any) => console.log(err)
				);
		}
	}

	deleteDocumentLink(e: any) {
		const index = this.documents.findIndex(d => d.lookupId === e.row.data.lookupId);
		this.documents.splice(index, 1);

		return false;
	}

	deleteEvent(e: any) {
		if (confirm("Are you sure you wish to delete this event?")) {
			this.machineEventService.deleteSingleById(e.row.data.id)
				.subscribe(
					() => {
						this.toastService.success("Event Deleted");
						this.getEvents();
					},
					(err: any) => console.log(err)
				);
		}
	}

	doesEventHaveImage(e: any) {
		return (e.row.data.eventImageUrl === null || e.row.data.eventImageUrl === undefined || e.row.data.eventImageUrl === "") === false;
	}

	doesEventHaveThirdPartyReport(e: any) {
		return (e.row.data.thirdPartyReportLookupId === null || e.row.data.thirdPartyReportLookupId === undefined || e.row.data.thirdPartyReportLookupId === Guid.EMPTY) === false;
	}

	downloadEventThirdPartyReportLink(e: any) {
		const lookupId = Guid.parse(e.row.data.thirdPartyReportLookupId);
		if (lookupId === null || lookupId === undefined) {
			return;
		}
		this.fileService.downloadFile(lookupId).subscribe(
			(response: any) => {
				const dataType = response.type;
				const binaryData = [];
				binaryData.push(response);
				const downloadLink = document.createElement("a");
				downloadLink.href = window.URL.createObjectURL(new Blob(binaryData, {type: dataType}));
				downloadLink.setAttribute("download", "Third Party Report for Machine Event #" + e.row.data.id);
				document.body.appendChild(downloadLink);
				downloadLink.click();
			}
		);
		return false;
	}

	downloadFileLink(e: any) {
		const lookupId = Guid.parse(e.row.data.lookupId);
		this.fileService.downloadFile(lookupId).subscribe(
			(response: any) => {
				const dataType = response.type;
				const binaryData = [];
				binaryData.push(response);
				const downloadLink = document.createElement("a");
				downloadLink.href = window.URL.createObjectURL(new Blob(binaryData, {type: dataType}));
				downloadLink.setAttribute("download", e.row.data.fileName);
				document.body.appendChild(downloadLink);
				downloadLink.click();
			}
		);
		return false;
	}

	edit() {
		this.readOnly = false;
		this.buildButtons("edit");
		this.title = "Edit " + this.itemType;
		this.toastService.warn("You are now editing...");
	}
	
	editAvailability(e: any) {
		const availabilityToEdit = this.machineAvailabilities.find((f)=> f.id == e.row.data.id);
		if (availabilityToEdit !== null && availabilityToEdit !== undefined) {
			this.popupMachineAvailability = availabilityToEdit;
		} else {
			return;
		}
		this.popupTitle = "Edit Availability";
		this.popupAddButtonOptions = { onClick: () => this.updateAvailability(), text: "Save Changes", visible: true };
		this.popupVisible = true;
	}
	
	editCriticalSpare(e: any) {
		const criticalSpareToEdit = this.criticalSpares.find((f)=> f.id == e.row.data.id);
		if (criticalSpareToEdit !== null && criticalSpareToEdit !== undefined) {
			this.popupCriticalSpare = criticalSpareToEdit;
		} else {
			return;
		}
		this.popupTitle = "Edit Critical Spare";
		this.popupAddButtonOptions = { onClick: () => this.updateCriticalSpare(), text: "Save Changes", visible: true };
		this.popupVisible = true;
	}
	
	editEvent(e: any) {
		const eventToEdit = this.events.find((f)=> f.id == e.row.data.id);
		if (eventToEdit !== null && eventToEdit !== undefined) {
			this.popupEvent = eventToEdit;			
		} else {
			return;
		}
		this.popupTitle = "Edit Event";
		this.popupAddButtonOptions = { onClick: () => this.updateEvent(), text: "Save Changes", visible: true };
		this.popupVisible = true;
	}

	getAvailability() {
		this.machineAvailabilityService.forMachine(this.item.id)
			.subscribe(
				(res: any) => this.machineAvailabilities = res.response,
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				}
			);
	}

	getCriticalSpares() {
		this.criticalSpareService.forMachineId(this.item.id)
			.subscribe(
				(res: any) => this.criticalSpares = res.response,
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				}
			);
	}

	getEvents() {
		this.machineEventService.getByMachineId(this.item.id)
			.subscribe(
				(res: any) => this.events = res.response,
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				}
			);
	}

	getItem(itemId: number) {
		this.itemService.getSingleById(itemId)
			.subscribe(
				(res: any) => {
					this.item = res.response;
					this.documents = JSON.parse(this.item.documents);
					this.getAvailability();
					this.getEvents();
					this.getQuestionnaires();
					this.getOpsActionAudits(itemId);
					this.getRequests();
					this.getCriticalSpares();
				},
				(err) => {console.log(err); this.toastService.error("Something Went Wrong!");}
			);
	}

	getOpsActionAudits(workOrderId: number)	{
		this.opsActionAuditService.getByMachineId(workOrderId)
			.subscribe(
				(res: any) => {
					this.opsActionAudits = res.response;
				},
				(err) => console.log(err)
			);
	}

	getPurchasableItems() {		
		this.purchasableItemService.getAll()
			.subscribe(
				(res: any) => {
					this.purchasableItems = res.response.items; 
					this.purchasableItems = this.purchasableItems.filter((pi) => pi.purchaseType === PurchaseTypeEnum.CriticalSpares);
					this.purchasableItems.sort((a,b) => a.name.localeCompare(b.name));
				},
				(err) => console.log(err)
			);
	}

	getQuestionnaires() {
		this.machineQuestionnaireService.getByMachineId(this.item.id)
			.subscribe(
				(res: any) => this.questionnaires = res.response,
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				}
			);
	}

	getRequests() {
		this.machineRequestService.getByMachineId(this.item.id)
			.subscribe(
				(res: any) => this.requests = res.response,
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				}
			);
	}

	getSites() {		
		this.siteService.getAll()
			.subscribe(
				(res: any) => this.sites = res.response.items,
				(err) => console.log(err)
			);
	}

	getSuppliers() {		
		this.supplierService.getAll()
			.subscribe(
				(res: any) => {
					this.suppliers = res.response.items; 
					this.suppliers.sort((a,b) => a.name.localeCompare(b.name));
				},
				(err) => console.log(err)
			);
	}

	getUserProfiles() {		
		this.userProfileService.getAllWithUsers()
			.subscribe(
				(res: any) => { 
					this.people = res.response;
					this.people.sort((a,b) => a.fullName.localeCompare(b.fullName));
				},
				(err) => console.log(err)
			);
	}

	headerButtonClick(method: any) {
		// @ts-ignore // Required to be able to call the method directly from the variable
		if (this[method]) this[method]();
	}

	updateAvailability() {
		this.machineAvailabilityService.updateSingleById(this.popupMachineAvailability.id, this.popupMachineAvailability)
			.subscribe(
				() => {
					this.popupVisible = false;
					this.getAvailability();
					this.toastService.success("Successfully Edited Availability");
				},
				(err: any) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				} 
			);
	}

	updateCriticalSpare() {
		this.criticalSpareService.updateSingleById(this.popupCriticalSpare.id, this.popupCriticalSpare)
			.subscribe(
				() => {
					this.popupVisible = false;
					this.getCriticalSpares();
					this.toastService.success("Successfully Edited Critical Spare");
				},
				(err: any) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				} 
			);
	}

	updateEvent() {
		this.machineEventService.updateSingleById(this.popupEvent.id, this.popupEvent)
			.subscribe(
				() => {
					this.popupVisible = false;
					this.popupEvent = new MachineEvent();
					this.getEvents();
					this.toastService.success("Successfully Edited Event");
				},
				(err: any) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				} 
			);
	}

	updateItem() {
		this.item.documents = JSON.stringify(this.documents);
		this.itemService.updateSingleById(this.item.id, this.item)
			.subscribe(
				() => {
					this.toastService.success("Successfully Updated " + this.itemType);
					this.getItem(this.item.id);
				},
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				}
			);
	}

	uploadDocument(file: File) {
		const containerName = "machinefiles";
		const uploadData = new FormData();
		uploadData.append("containerName", containerName);
		uploadData.append("files", file);
		// Add Mime Type key when mime types are decided upon...
		// uploadData.append("mimeTypeKey", "CADFileMimeTypes");
		this.fileService.uploadFile(uploadData)
			.subscribe(
				(res: any) => {
					this.popupDocument.lookupId = res.response[0].lookupId;
					this.popupDocument.fileName = res.response[0].name;
				},
				(err) => {
					console.log(err);
					this.toastService.error("Document Upload Failed - Please ensure you are uploading a valid file");
				}
			);
	}

	uploadEventImage(file: File) {
		const containerName = "images";
		const uploadData = new FormData();
		uploadData.append("containerName", containerName);
		uploadData.append("files", file);
		uploadData.append("mimeTypeKey", "ImageMimeTypes");
		this.fileService.uploadFile(uploadData)
			.subscribe(
				(res: any) => {
					const eventImageUrl = `${environment.server.storageUrl}/${containerName}/${res.response[0].location}`;
					this.popupEvent.eventImageUrl = eventImageUrl;
					this.toastService.success("Image uploaded - Click Save to confirm change");
				},
				(err) => {
					console.log(err);
					this.toastService.error("Image Upload Failed - Please ensure you are uploading a valid image file");
				}
			);
	}

	uploadEventThirdPartyReport(file: File) {
		const containerName = "machineeventreports";
		const uploadData = new FormData();
		uploadData.append("containerName", containerName);
		uploadData.append("files", file);
		this.fileService.uploadFile(uploadData)
			.subscribe(
				(res: any) => {
					this.popupEvent.thirdPartyReportLookupId = res.response[0].lookupId;
					this.toastService.success("Report uploaded - Click Save to confirm change");
				},
				(err) => {
					console.log(err);
					this.toastService.error("Report Upload Failed - Please ensure you are uploading a valid file");
				}
			);
	}

	viewEventImage(e: any) {
		const imageUrl = e.row.data.eventImageUrl;
		const viewLink = document.createElement("a");
		viewLink.href = imageUrl;
		document.body.appendChild(viewLink);
		viewLink.click();
		return false;
	}

	viewPFMEAs() {		
		this.router.navigate([`admin/machines/${this.item.id}/pfmeas`]);
	}
}